import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Mail } from "lucide-react";

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/contact").then((res) => setMessages(res.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Messaggio eliminato");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors">
            <ArrowLeft size={15} strokeWidth={1.5} /> Torna
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Posta in arrivo</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-10">
          Messaggi ricevuti ({messages.length})
        </h1>

        {loading ? (
          <p className="font-ui text-sm text-[#57534E] py-10">Caricamento…</p>
        ) : messages.length === 0 ? (
          <div className="border border-dashed border-[#E7E5E4] py-20 text-center">
            <Mail size={30} strokeWidth={1.2} className="mx-auto text-[#A8A29E] mb-4" />
            <p className="font-display text-2xl italic text-[#57534E]">Nessun messaggio per ora.</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="messages-list">
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`} className="border border-[#E7E5E4] bg-[#F2F0EB] p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display text-2xl text-[#1C1917]">{m.name}</span>
                      <span className="label-ui text-[#9F583F] border border-[#e6cbc0] px-2 py-0.5">{m.topic}</span>
                    </div>
                    <a href={`mailto:${m.email}`} className="font-ui text-sm text-[#57534E] hover:text-[#9F583F] transition-colors break-all">
                      {m.email}
                    </a>
                    {m.company && <span className="font-ui text-sm text-[#57534E]"> · {m.company}</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="label-ui text-[#A8A29E]">{formatDate(m.created_at)}</span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-message-${m.id}`} className="text-[#A8A29E] hover:text-red-600 transition-colors" title="Elimina">
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none bg-[#F9F8F6] border-[#E7E5E4]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display text-2xl">Eliminare il messaggio?</AlertDialogTitle>
                          <AlertDialogDescription className="font-body">Questa azione non è reversibile.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none label-ui">Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(m.id)} data-testid="confirm-delete-message" className="rounded-none bg-red-700 hover:bg-red-800 label-ui">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <p className="font-body text-[#292524] leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
